// parse-text/index.ts — public surface for the parse-text bundle.
//
// Output is an IIFE (build-parse-text-bundle.mjs) loaded as a footer <script>, after core-helpers.js
// (iconMap / escapeHtml / checkIfValidUUID / BASE_URL / the *_CATALOG boot island), icon-bundle.js and
// rune-icon-bundle.js (the table-mount hydrate re-runs ftMountIcons / ftMountRuneIcons over injected
// table HTML). It defines + self-runs window.ftMountParsedText(root?), the placeholder scanner that
// retires PHP parse_text() page rendering.
//
// Mount contract (mirrors ftMountIcons / ftMountRuneIcons, includes/js-src/icon/index.ts):
// PHP's ft_parse_text_mount() (includes/functions/parse_text/ft_parse_text_mount_inc.php) emits:
//   <span class="ft-pt-mount"><script type="application/json" class="ft-pt-blob">{"text":"…raw…"}</script></span>
// The scanner reads the RAW text, renders it through the shared parseText() port, replaces the <span>
// with the rendered nodes, then hydrates any [table:]/[random_table:] mounts + icons/runes inside — all
// idempotently. The <script type="application/json"> payload (not a plain inline <script>) survives the
// innerHTML insertion an AJAX side panel / WS push uses.
//
// AJAX/WS re-hydration is by EXPLICIT scoped re-invocation: window.ftMountParsedText(scope) (full-text
// mounts) + window.ftMountParseTextTables(scope) (bare table mounts from object-card etc.), wired next
// to the ftMountRuneIcons / ftMountThumbnails calls in the footer + template.js.

import { parseText, ftMountParseTextTables } from '../shared/parse-text';

declare global {
    interface Window {
        ftMountParsedText?: (root?: Element | Document) => void;
        ftMountParseTextTables?: (root?: Element | Document) => void;
        ftMountIcons?: (root?: Element | Document) => void;
        ftMountRuneIcons?: (root?: Element | Document) => void;
    }
}

// Scan `root` (default document) for un-mounted parse-text placeholders; render each via parseText() and
// replace the <span class="ft-pt-mount"> with the result, then hydrate table mounts + icons + runes.
function ftMountParsedText(root?: Element | Document): void {
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('span.ft-pt-mount:not([data-pt-mounted])');
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-pt-mounted', '1'); // mark first so a thrown render never loops on rescan
        try {
            const blob = el.querySelector('script.ft-pt-blob');
            const raw = blob ? (blob.textContent || '') : '';
            const text = raw ? String((JSON.parse(raw) || {}).text ?? '') : '';
            const parent = el.parentNode as (Element | Document) | null;
            if (!parent) continue;

            const html = parseText(text); // '' for empty → element simply drops
            const tpl = document.createElement('template');
            tpl.innerHTML = html;
            const frag = tpl.content;
            parent.replaceChild(frag, el); // replaces the placeholder with the rendered nodes

            // Hydrate the freshly-injected subtree: [table:]/[random_table:] mounts, then icon + rune
            // placeholders inside the parsed text / inside the (later) injected table HTML.
            ftMountParseTextTables(parent);
            if (typeof window.ftMountIcons === 'function') window.ftMountIcons(parent);
            if (typeof window.ftMountRuneIcons === 'function') window.ftMountRuneIcons(parent);
        } catch (e) {
            console.warn('ftMountParsedText: mount failed', e);
        }
    }
}

window.ftMountParsedText = ftMountParsedText;
window.ftMountParseTextTables = ftMountParseTextTables;

// Render page-load placeholders as soon as the bundle executes (footer <script>, so every placeholder
// above it is parsed), and again on DOMContentLoaded for any that came after. Idempotent: a mounted
// placeholder is replaced outright (and table mounts mark data-pt-done), so the second pass is a no-op.
//
// Run BOTH scanners: ftMountParsedText() handles .ft-pt-mount (full rich-text fields); ftMountParseTextTables()
// also handles BARE .ft-pt-table-mount spans emitted directly by ft_table_mount() on server-rendered pages
// (item.php table/random_table items, character_wealth_by_level, the character-creation tables, the
// award-experience form) — those are not wrapped in a .ft-pt-mount, so the first scanner would miss them.
function ftRunParseTextScanners(): void {
    ftMountParsedText();
    ftMountParseTextTables();
}
if (document.readyState !== 'loading') {
    ftRunParseTextScanners();
} else {
    document.addEventListener('DOMContentLoaded', ftRunParseTextScanners);
}

export {};
